import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

// FullCalendar Imports
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import { environment } from '../../environments/environment';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatCardActions } from "@angular/material/card";
import { PrenotazioneDialogComponent } from './prenotazione-dialog/prenotazione-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-calendario-operatore',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    FullCalendarModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCardActions
  ],
  templateUrl: './calendario-operatore.component.html',
  styleUrls: ['./calendario-operatore.component.css']
})
export class CalendarioOperatoreComponent implements OnInit {

  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  loading: boolean = false;
  listaCalendari: any[] = [];
  calendarioSelezionato: any = null;
  hasEvents: boolean = false;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'timeGridWeek',
    locale: itLocale,

    // CAMBIO FONDAMENTALE: 
    // Invece di 'auto', diamo un'altezza fissa o '100%' 
    // se il wrapper ha min-height nel CSS
    height: '100%',
    slotMinTime: '08:00:00',
    slotMaxTime: '20:00:00',

    // Aumentiamo l'altezza delle righe (più spazio verticale per slot)
    slotEventOverlap: false,
    allDaySlot: false,

    // Miglioriamo la visibilità della griglia
    slotDuration: '00:15:00',
    slotLabelInterval: '01:00:00',
    eventDidMount: (info) => {
      // Recuperiamo la nota o lo status dalle extendedProps che abbiamo settato in caricaSlot
      const note = info.event.extendedProps['note'];
      const status = info.event.extendedProps['status'];
      const isMerged = info.event.extendedProps['isMerged'];

      let tooltipText = '';

      if (status === 'free') {
        tooltipText = 'Slot disponibile per la prenotazione';
      } else if (isMerged) {
        tooltipText = `Appuntamento: ${note || 'Nessuna nota'}`;
      } else {
        tooltipText = note || 'Slot occupato';
      }

      // Aggiungiamo l'attributo title standard del browser (il tooltip più semplice)
      // Se vuoi quelli di Angular Material, vedi il punto 3 sotto.
      info.el.setAttribute('title', tooltipText);

      // Utile per il CSS che avevi prima
      info.el.setAttribute('data-status', status);
    },
    eventClick: this.handleEventClick.bind(this),
    expandRows: true,              // Espande le righe per riempire lo spazio
    stickyHeaderDates: true,       // Mantiene i giorni visibili durante lo scroll
    nowIndicator: true,
    slotLabelFormat: {             // Rende l'orario laterale più grande
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      meridiem: false
    },
    eventClassNames: (arg) => {
      const isSelected = this.slotSelezionati.some(s => s.id === arg.event.id);
      return isSelected ? ['slot-selezionato-attivo'] : [];
    },
    // Opzionale: per rendere il cursore a manina sugli slot liberi
    eventMouseEnter: (info) => {
      if (info.event.extendedProps['status'] === 'free') {
        info.el.style.cursor = 'pointer';
      }
    }
  };

  listaSlotTotale: any[] | undefined;
  slotSelezionati: any[] = [];

  constructor(
    private http: HttpClient,
    private dialogRef: MatDialogRef<CalendarioOperatoreComponent>,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { operatoreId: number, qualificaId: number }
  ) { }

  ngOnInit(): void {
    // Ascolta i cambiamenti di dimensione della finestra o dei contenitori
    const resizeObserver = new ResizeObserver(() => {
      if (this.calendarComponent) {
        this.calendarComponent.getApi().updateSize();
      }
    });

    // Osserva il body o il contenitore del calendario
    resizeObserver.observe(document.body);
    this.caricaListaCalendari();
  }

  // Verifica se il nuovo slot è attaccato a quelli già selezionati
  private isContiguo(nuovo: any): boolean {
    return this.slotSelezionati.some(s =>
      (nuovo.start.getTime() === s.end.getTime()) || (nuovo.end.getTime() === s.start.getTime())
    );
  }

  caricaListaCalendari(): void {
    this.loading = true;
    const url = environment.API_ENDPOINT_COT_SERVICE + `/calendari/operatore-ruolo`;

    console.log("carica calendari" + url)

    const params = new HttpParams()
      .set("ruoloId", this.data.qualificaId)
      .set("operatoreId", this.data.operatoreId);

    this.http.get<any[]>(url, { params }).subscribe({
      next: (res) => {
        this.listaCalendari = res;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  selezionaCalendario(cal: any): void {
    this.calendarioSelezionato = cal;
    this.caricaSlot(cal.id);
  }

  caricaSlot(calendarioId: number): void {
    this.loading = true;
    this.cdr.detectChanges(); // Forza lo spinner a video

    this.http.get<any[]>(environment.API_ENDPOINT_COT_SERVICE + `/calendari/slots/${calendarioId}`).subscribe({
      next: (slots) => {
        this.listaSlotTotale = slots;
        const mappedEvents: EventInput[] = [];

        // 1. Raggruppamento per Appuntamento
        // Creiamo una mappa dove la chiave è l'ID dell'appuntamento
        const gruppiAppuntamento = new Map<number, any[]>();
        const slotLiberi: any[] = [];

        slots.forEach(s => {
          // Verifichiamo se lo slot è associato a un appuntamento (ad es. tramite l'oggetto o l'id)
          // Adatto la condizione in base a quanto abbiamo detto: cot_appuntamento
          if (s.status !== 'free' && s.appuntamentoId) {
            const appId = s.appuntamentoId;
            if (!gruppiAppuntamento.has(appId)) {
              gruppiAppuntamento.set(appId, []);
            }
            gruppiAppuntamento.get(appId)?.push(s);
          } else {
            // Se è libero o se è busy ma non ha un ID appuntamento (blocco manuale singolo)
            slotLiberi.push(s);
          }
        });

        // 2. Mapping Slot Liberi (rimangono blocchi singoli da 15 min)
        slotLiberi.forEach(s => {
          mappedEvents.push({
            id: s.id.toString(),
            start: s.startAt,
            end: s.endAt,
            title: s.status === 'free' ? 'LIBERO' : (s.note ? s.note : 'OCCUPATO'),
            backgroundColor: s.status === 'free' ? '#ffffff' : '#f8d7da',
            textColor: s.status === 'free' ? '#0059b3' : '#721c24',
            borderColor: '#0059b3',
            extendedProps: {
              status: s.status,
              note: s.note,
              isSingle: true
            }
          });
        });

        // 3. Mapping Appuntamenti Accorpati (diventano un unico blocco)
        gruppiAppuntamento.forEach((slotsDelGruppo, appId) => {
          // Ordiniamo gli slot per tempo così da prendere correttamente inizio e fine
          slotsDelGruppo.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

          const primo = slotsDelGruppo[0];
          const ultimo = slotsDelGruppo[slotsDelGruppo.length - 1];

          mappedEvents.push({
            id: `app-${appId}`, // ID univoco per il blocco accorpato
            start: primo.startAt,
            end: ultimo.endAt, // Fine dell'ultimo slot
            title: primo.note || 'APPUNTAMENTO',
            backgroundColor: '#e6f2ff', // Un colore diverso per gli appuntamenti completi
            textColor: '#003366',
            borderColor: '#b3d7ff',
            extendedProps: {
              status: 'busy',
              appuntamentoId: appId,
              note: primo.note,
              isMerged: true,
              numSlots: slotsDelGruppo.length
            }
          });
        });

        this.hasEvents = slots.length > 0;
        this.loading = false;
        this.cdr.detectChanges();

        setTimeout(() => {
          if (this.calendarComponent) {
            const calendarApi = this.calendarComponent.getApi();
            calendarApi.removeAllEvents();
            calendarApi.addEventSource(mappedEvents);

            // 3. Refresh dimensionale per adattarsi al nuovo spazio
            calendarApi.updateSize();
          } else {
            // Fallback: se non c'è ancora la ViewChild, passiamo i dati tramite options
            this.calendarOptions.events = mappedEvents;
            this.cdr.detectChanges();
          }
        }, 0);
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  tornaAllaLista(): void {
    this.calendarioSelezionato = null;
    this.calendarOptions.events = [];
  }

  chiudi(): void { this.dialogRef.close(); }

  handleEventClick(info: any): void {
    const event = info.event;

    // 1. Se lo slot non è libero, non fare nulla
    if (event.extendedProps['status'] !== 'free') return;

    const slotId = event.id;
    const index = this.slotSelezionati.findIndex(s => s.id === slotId);

    if (index > -1) {
      // DESELEZIONE: Se clicco un evento già selezionato, lo tolgo
      this.slotSelezionati.splice(index, 1);
    } else {
      // SELEZIONE: Aggiungo lo slot
      const nuovoSlot = {
        id: slotId,
        start: event.start,
        end: event.end,
        title: event.title
      };

      // Validazione contiguità (Opzionale ma consigliata)
      if (this.slotSelezionati.length > 0) {
        if (!this.isContiguo(nuovoSlot)) {
          alert("Puoi selezionare solo slot contigui (senza buchi temporali).");
          return;
        }
      }

      this.slotSelezionati.push(nuovoSlot);
    }

    // Ordiniamo sempre gli slot per tempo di inizio
    this.slotSelezionati.sort((a, b) => a.start.getTime() - b.start.getTime());

    // Forziamo FullCalendar a rinfrescare le classi CSS degli eventi
    this.calendarComponent.getApi().refetchEvents();
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.render();
  }

  // Nuovo metodo di supporto per pulire il codice
  private finalizzaRenderingCalendario(): void {
    // Usiamo un doppio frame per assicurarci che il browser abbia finito di chiudere la dialog
    setTimeout(() => {
      if (this.calendarComponent) {
        const api = this.calendarComponent.getApi();
        api.updateSize();

        // Solo ora togliamo il caricamento
        setTimeout(() => {
          this.loading = false;
        }, 50);
      } else {
        this.loading = false;
      }
    }, 400); // Leggermente superiore al tempo di chiusura della modale (300ms)
  }

  confermaPrenotazioneMultipla(): void {
    const dialogRef = this.dialog.open(PrenotazioneDialogComponent, {
      width: '450px',
      data: {
        isMultiplo: true,
        count: this.slotSelezionati.length,
        inizio: this.slotSelezionati[0].start,
        fine: this.slotSelezionati[this.slotSelezionati.length - 1].end
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loading = true;

        const calendarApi = this.calendarComponent.getApi();
        const dataCorrente = calendarApi.getDate();

        // Prepariamo i dati per l'appuntamento che copre tutti gli slot
        const payload = {
          startAt: this.slotSelezionati[0].start,
          endAt: this.slotSelezionati[this.slotSelezionati.length - 1].end,
          slotIds: this.slotSelezionati.map(s => s.id),
          note: result.note,
          cf: result.cf
        };

        const url = `${environment.API_ENDPOINT_COT_SERVICE}/calendari/prenota-multiplo`;

        this.http.post(url, payload, { responseType: 'text' }).subscribe({
          next: () => {
            this.slotSelezionati = [];
            this.caricaSlot(this.calendarioSelezionato.id);

            setTimeout(() => {
              if (this.calendarComponent) {
                const api = this.calendarComponent.getApi();
                api.gotoDate(dataCorrente);
                api.updateSize();
              }
            }, 500);

            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            // Se l'errore era solo di parsing ma l'operazione è riuscita, 
            // l'appuntamento lo vedrai comunque al refresh.
            console.error(err);
          }
        });
      }
    });
  }

  cancellaSelezione(): void {
    // 1. Svuota l'array logico
    this.slotSelezionati = [];

    // 2. Forza FullCalendar a ridisegnare gli eventi
    // Questo farà scattare nuovamente la funzione 'eventClassNames' per ogni slot
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      calendarApi.render();
    }

    // 3. Notifica ad Angular che il footer deve sparire (visto che slotSelezionati.length ora è 0)
    this.cdr.detectChanges();
  }
}

export interface SlotCalendario {
  oid_2: number;
  start_at: Date;
  end_at: Date;
  status: 'free' |
  'busy' |
  'busy_unavailable' |
  'busy_tentative'
}

export interface Calendario {
  oid_2: number;
  nome: string;
  attiva: string;
  slots: SlotCalendario[];
}